import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery,
    Tabs, Tab, Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AiFillBulb } from "react-icons/ai";

// ─── Styled Components (matching CppFundamentals pattern) ─────
const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const InfoPill = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700,
        fontSize: "0.72rem", height: 22, border: `1px solid ${color}44`,
        "& .MuiChip-label": { px: 1 }
    }} />
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto" sx={{
        mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
        "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
        "& .Mui-selected": { color: "goldenrod !important" },
        "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
    }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

// ─── CONCEPT CARD ─────────────────────────────────────────────
const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px", p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

// ─── ALL DATA ─────────────────────────────────────────────────

// ── SECTION 1: Function Declaration & Definition ──────────────
const declDefTabs = [
    {
        label: "Declaration (Prototype)",
        code: `// ── Function DECLARATION (Prototype) ─────────────────────────
// Tells the compiler: "this function exists, here is its signature"
// Must appear BEFORE the function is called
// Can be in a header file (.h) or at top of .cpp

#include <iostream>

// Prototype — return_type name(param_types);
int    add(int a, int b);          // returns int,  2 int params
void   greet(std::string name);    // returns nothing
double power(double base, int exp); // returns double
bool   isEven(int n);              // returns bool

int main() {
    std::cout << add(3, 4);   // OK — compiler saw prototype above
    return 0;
}

// Actual definition can be BELOW main() — prototype handles forward reference
int add(int a, int b) { return a + b; }`,
    },
    {
        label: "Definition (Body)",
        code: `// ── Function DEFINITION ─────────────────────────────────────
// The actual implementation — provides the function body

//  return_type  name ( parameters ) { body }
//  ──────────┐  ──┐   ──────────┐   ───┐
//            ↓   ↓             ↓     ↓
    int      add (int a, int b) { return a + b; }

// ── More examples ─────────────────────────────────────────────
void printLine(int n) {
    for (int i = 0; i < n; ++i)
        std::cout << '-';
    std::cout << '\\n';
    // void functions may omit return, or use: return;
}

double circleArea(double r) {
    const double PI = 3.14159265358979;
    return PI * r * r;   // MUST return a value (non-void)
}

// ── Inline function (hint to compiler to expand in-place) ─────
inline int square(int x) { return x * x; }`,
    },
    {
        label: "Declaration vs Definition",
        code: `// ── DECLARATION vs DEFINITION — the key difference ──────────
//
//  Declaration:  "I promise this exists"   →  no body, ends with ;
//  Definition:   "Here is the actual code"  →  has { body }
//
//  A function can be DECLARED many times but DEFINED only ONCE
//  (One Definition Rule — ODR)

// ✅ Multiple declarations allowed (e.g., in headers):
int multiply(int, int);    // forward declaration
int multiply(int, int);    // again — fine

// ✅ Exactly ONE definition:
int multiply(int a, int b) {
    return a * b;
}

// ❌ Two definitions = linker error:
// int multiply(int a, int b) { return a + b; } // duplicate!

// ── Combined (define where you declare) ───────────────────────
// When the function is used below its definition, no prototype needed:
int subtract(int a, int b) { return a - b; }  // defined above main

int main() {
    subtract(10, 3);  // OK — already defined above
}`,
    },
];

// ── SECTION 2: Function Invocation ───────────────────────────
const invocationTabs = [
    {
        label: "Basic Call",
        code: `#include <iostream>

int    add(int a, int b)        { return a + b; }
double divide(double a, double b) {
    if (b == 0) throw std::runtime_error("divide by zero");
    return a / b;
}
void   printResult(int r)       { std::cout << "Result: " << r << '\\n'; }

int main() {
    // ── Calling a function ─────────────────────────────────────
    int sum = add(5, 3);          // capture return value
    printResult(sum);             // pass return value as argument
    printResult(add(10, 20));     // call directly inside another call

    // ── Chaining / nesting calls ──────────────────────────────
    int total = add(add(1, 2), add(3, 4));  // = add(3, 7) = 10

    // ── Ignoring return value (valid but consider if intentional) ─
    add(1, 2);  // result discarded — no compiler error, just wasteful

    // ── Calling with expressions ──────────────────────────────
    int x = 4, y = 6;
    printResult(add(x * 2, y / 2));  // add(8, 3) = 11

    return 0;
}`,
    },
    {
        label: "Return Values",
        code: `#include <iostream>
#include <string>

// ── Single return value ───────────────────────────────────────
int max2(int a, int b) {
    return (a > b) ? a : b;
}

// ── Multiple values via std::pair / std::tuple ────────────────
#include <utility>
#include <tuple>

std::pair<int,int> minMax(std::vector<int>& v) {
    int mn = *std::min_element(v.begin(), v.end());
    int mx = *std::max_element(v.begin(), v.end());
    return {mn, mx};  // return two values as a pair
}

std::tuple<int,double,std::string> getStats() {
    return {42, 3.14, "hello"};
}

int main() {
    // Unpack pair:
    auto [lo, hi] = minMax(v);  // C++17 structured binding

    // Unpack tuple:
    auto [count, avg, name] = getStats();

    // ── Early return (guard clause) ────────────────────────────
    auto safeDivide = [](int a, int b) -> double {
        if (b == 0) return 0.0;     // early exit
        return static_cast<double>(a) / b;
    };
    return 0;
}`,
    },
    {
        label: "Recursive Calls",
        code: `#include <iostream>

// ── Recursion: function calls ITSELF ─────────────────────────
// Every recursive function needs:
//   1. Base case  — stops recursion
//   2. Recursive case — calls itself with smaller input

int factorial(int n) {
    if (n <= 1) return 1;          // BASE CASE
    return n * factorial(n - 1);   // RECURSIVE CASE
}
// factorial(5) → 5 * factorial(4)
//              → 5 * 4 * factorial(3)
//              → 5 * 4 * 3 * 2 * 1 = 120

// ── Fibonacci (classic — but use DP for performance) ─────────
int fib(int n) {
    if (n <= 1) return n;                  // base cases: fib(0)=0, fib(1)=1
    return fib(n - 1) + fib(n - 2);       // O(2^n) — slow without memo!
}

// ── Tail-recursive factorial (compiler can optimise) ─────────
int factTail(int n, int acc = 1) {
    if (n <= 1) return acc;
    return factTail(n - 1, n * acc);  // last action is the recursive call
}

int main() {
    std::cout << factorial(5);   // 120
    std::cout << fib(10);        // 55
    std::cout << factTail(6);    // 720
}`,
    },
];

// ── SECTION 3: Local & Global Scope ──────────────────────────
const scopeTabs = [
    {
        label: "Local Scope",
        code: `#include <iostream>

void demoLocalScope() {
    int x = 10;           // LOCAL to demoLocalScope — created here

    {
        int y = 20;       // LOCAL to this inner block — only exists here
        std::cout << x;   // ✅ x visible — outer scope accessible
        std::cout << y;   // ✅ y visible — same scope
    }
    // std::cout << y;    // ❌ ERROR — y destroyed when inner block ended!

    if (true) {
        int z = 30;       // LOCAL to if-block
    }
    // z is gone here ↑

    for (int i = 0; i < 3; ++i) {
        // i is LOCAL to the for loop — C++: loop variable stays in loop
    }
    // std::cout << i;   // ❌ i out of scope here

    std::cout << x;      // ✅ still alive — function hasn't returned yet
}   // x destroyed here — function ends

int main() {
    demoLocalScope();
    // std::cout << x;   // ❌ x is in demoLocalScope, not here!
}`,
    },
    {
        label: "Global Scope",
        code: `#include <iostream>

// ── GLOBAL variables — declared outside ALL functions ─────────
int globalCounter = 0;            // visible to ALL functions in this file
const double PI   = 3.14159;      // global constant — safe, read-only

void increment() {
    ++globalCounter;              // ✅ global — accessible here
    std::cout << globalCounter;
}

void reset() {
    globalCounter = 0;            // ✅ any function can modify it
}

// ── Name conflict: local shadows global ──────────────────────
int value = 100;   // global

void shadowDemo() {
    int value = 999;              // LOCAL — shadows the global!
    std::cout << value;           // prints 999 (local wins)
    std::cout << ::value;         // prints 100 — :: forces global scope
}

int main() {
    increment(); increment();     // globalCounter = 2
    std::cout << globalCounter;   // 2

    shadowDemo();                 // shows shadowing

    // ── Best practice: avoid global mutable state ─────────────
    // Prefer passing values through function parameters instead
    return 0;
}`,
    },
    {
        label: "Static Variables",
        code: `#include <iostream>

// ── static LOCAL: persists between calls, scoped to function ─
void countCalls() {
    static int calls = 0;    // initialised ONCE — survives across calls!
    ++calls;
    std::cout << "Called " << calls << " times\\n";
}

// ── static in class: shared across all instances ──────────────
class Counter {
public:
    static int total;           // declaration — one shared copy
    Counter() { ++total; }
};
int Counter::total = 0;         // definition (must be outside class)

// ── Namespace scope — avoids global name collisions ───────────
namespace Math {
    double PI = 3.14159;
    double circleArea(double r) { return PI * r * r; }
}

namespace Physics {
    double PI = 3.14159265358979;  // different PI — no conflict!
}

int main() {
    countCalls();   // Called 1 times
    countCalls();   // Called 2 times
    countCalls();   // Called 3 times

    Counter a, b, c;
    std::cout << Counter::total;   // 3 — static shared across all

    std::cout << Math::PI;         // 3.14159
    std::cout << Physics::PI;      // 3.14159265358979
    Math::circleArea(5.0);
}`,
    },
    {
        label: "Scope Rules Summary",
        code: `// ── C++ SCOPE RESOLUTION RULES ──────────────────────────────
//
//  When a name is used, C++ searches in this order:
//    1. Current block / function scope         (innermost first)
//    2. Enclosing blocks (outward)
//    3. Class scope (if inside a method)
//    4. Namespace scope
//    5. Global scope
//    6. Compiler error if not found anywhere
//
//  Shadowing: inner scope can reuse an outer name — hides outer variable
//  :: operator: forces lookup in global/namespace scope

int x = 1;                    // global

namespace App {
    int x = 2;                // namespace scope

    void demo() {
        int x = 3;            // local
        {
            int x = 4;        // inner block
            std::cout << x;   // 4 (innermost)
            std::cout << App::x;  // 2 (namespace)
            std::cout << ::x;     // 1 (global)
        }
        std::cout << x;       // 3 (back to function local)
    }
}`,
    },
];

// ── SECTION 4: Function Parameters ───────────────────────────
const paramTabs = [
    {
        label: "Pass by Value",
        code: `#include <iostream>

// ── PASS BY VALUE — a COPY is made ───────────────────────────
// Changes inside the function do NOT affect the original!
void doubleIt(int n) {
    n = n * 2;              // modifies the COPY
    std::cout << n;         // 20 (copy is doubled)
}

void addTen(std::vector<int> v) {  // entire vector is COPIED — expensive!
    v.push_back(10);        // only the copy is changed
}

int main() {
    int x = 10;
    doubleIt(x);
    std::cout << x;         // still 10 — original unchanged!

    std::vector<int> nums = {1, 2, 3};
    addTen(nums);           // expensive copy of all 3 elements
    std::cout << nums.size(); // still 3 — original unchanged!

    // ── When to use pass-by-value ────────────────────────────
    // ✅ Primitive types (int, double, char, bool) — cheap to copy
    // ✅ When you want to work on a copy without affecting caller
    // ❌ Large objects (vector, string, struct) — costly copy!
}`,
    },
    {
        label: "Pass by Reference",
        code: `#include <iostream>
#include <vector>

// ── PASS BY REFERENCE — works on the ORIGINAL ────────────────
// & after the type means "reference to" — no copy is made

void doubleIt(int& n) {     // & = reference parameter
    n = n * 2;              // modifies the ORIGINAL
}

void appendItem(std::vector<int>& v, int item) {
    v.push_back(item);      // modifies the caller's vector directly
}

// ── CONST reference — read-only access, no copy ───────────────
// Use for large objects you need to READ but not modify
void printVector(const std::vector<int>& v) {  // no copy, no modify
    for (int x : v) std::cout << x << ' ';
    // v.push_back(9);   // ❌ ERROR — const ref, no modification!
}

int main() {
    int x = 10;
    doubleIt(x);
    std::cout << x;           // 20 — original WAS changed!

    std::vector<int> nums = {1, 2, 3};
    appendItem(nums, 99);
    std::cout << nums.size(); // 4 — original modified!

    printVector(nums);        // efficient — no copy of vector!

    // ── Rule of thumb ─────────────────────────────────────────
    //  Large objects to modify:     Type& param
    //  Large objects read-only:     const Type& param
    //  Primitives (int, bool...):   Type param (copy is fine)
}`,
    },
    {
        label: "Pass by Pointer",
        code: `#include <iostream>

// ── PASS BY POINTER — pass the ADDRESS of a variable ─────────
// Use when: optional parameter (can be nullptr), C API compatibility

void doubleIt(int* ptr) {
    if (ptr == nullptr) return;   // always check for null!
    *ptr = *ptr * 2;              // dereference to modify original
}

void swapValues(int* a, int* b) {
    int temp = *a;
    *a = *b;
    *b = temp;
}

// ── Pointer vs Reference ──────────────────────────────────────
//  Reference:  int& r = x;        can't be null, can't be reseated
//  Pointer:    int* p = &x;       can be null, can point elsewhere

void process(const int* data, int size) {
    for (int i = 0; i < size; ++i)
        std::cout << data[i] << ' ';
}

int main() {
    int x = 5;
    doubleIt(&x);         // pass ADDRESS with & operator
    std::cout << x;       // 10

    int a = 3, b = 7;
    swapValues(&a, &b);
    std::cout << a << ' ' << b;   // 7 3

    int arr[] = {10, 20, 30};
    process(arr, 3);      // arrays decay to pointer automatically
    // ── Prefer references over pointers in modern C++ ─────────
    // Pointers mainly for: C APIs, optional args, arrays/buffers
}`,
    },
    {
        label: "Default Parameters",
        code: `#include <iostream>
#include <string>

// ── DEFAULT PARAMETER VALUES ──────────────────────────────────
// Provide a value if caller doesn't supply one
// Defaults must be RIGHTMOST parameters

void greet(std::string name,
           std::string greeting = "Hello",  // has default
           char punct = '!')                // has default
{
    std::cout << greeting << ", " << name << punct << '\\n';
}

int createWindow(int width   = 800,
                 int height  = 600,
                 bool fullscreen = false) {
    // ...
    return 0;
}

int main() {
    greet("Alice");                      // Hello, Alice!
    greet("Bob", "Hi");                  // Hi, Bob!
    greet("Carol", "Hey", '.');          // Hey, Carol.

    createWindow();                      // 800x600 windowed
    createWindow(1920, 1080);            // 1920x1080 windowed
    createWindow(1920, 1080, true);      // 1920x1080 fullscreen

    // ❌ Can't skip a middle argument — defaults only at rightmost
    // greet("Dave", /*skip*/, '?');    // illegal!
}`,
    },
    {
        label: "Function Overloading",
        code: `#include <iostream>
#include <string>

// ── OVERLOADING — same name, different parameter types ────────
// Compiler chooses the right version based on argument types

int    add(int a, int b)          { return a + b; }
double add(double a, double b)    { return a + b; }
std::string add(std::string a, std::string b) { return a + b; }
int    add(int a, int b, int c)   { return a + b + c; }  // diff # of params

void print(int n)         { std::cout << "int: "    << n << '\\n'; }
void print(double d)      { std::cout << "double: " << d << '\\n'; }
void print(const char* s) { std::cout << "str: "    << s << '\\n'; }

int main() {
    add(3, 4);            // calls add(int,int)    → 7
    add(1.5, 2.5);        // calls add(double,double) → 4.0
    add("foo", "bar");    // calls add(string,string) → "foobar"
    add(1, 2, 3);         // calls add(int,int,int)   → 6

    print(42);            // int: 42
    print(3.14);          // double: 3.14
    print("hello");       // str: hello

    // ── Overloading rules ─────────────────────────────────────
    // ✅ Different number of params
    // ✅ Different param types
    // ❌ Return type alone — not enough to distinguish!
    // ❌ int f(int); double f(int);  → COMPILE ERROR
}`,
    },
    {
        label: "Lambda Functions",
        code: `#include <iostream>
#include <vector>
#include <algorithm>
#include <functional>

int main() {
    // ── Lambda syntax: [capture](params) -> return { body } ───
    auto square = [](int x) { return x * x; };
    std::cout << square(5);   // 25

    // ── Capture by value [=] ──────────────────────────────────
    int factor = 3;
    auto multiply = [factor](int x) { return x * factor; };  // copies factor
    factor = 99;                   // changing factor after capture
    std::cout << multiply(10);     // still 30 — captured OLD value

    // ── Capture by reference [&] ──────────────────────────────
    int sum = 0;
    auto accumulate = [&sum](int x) { sum += x; };  // modifies outer sum
    accumulate(10); accumulate(20); accumulate(30);
    std::cout << sum;  // 60 — outer sum WAS changed

    // ── Lambda with STL algorithms ────────────────────────────
    std::vector<int> v = {5,2,8,1,9,3};
    std::sort(v.begin(), v.end(), [](int a, int b){ return a > b; }); // descending
    // v = {9,8,5,3,2,1}

    std::vector<int> evens;
    std::copy_if(v.begin(), v.end(), std::back_inserter(evens),
                 [](int x){ return x % 2 == 0; });
    // evens = {8, 2}

    // ── std::function — store any callable ───────────────────
    std::function<int(int,int)> op = [](int a, int b){ return a+b; };
    std::cout << op(3, 4);  // 7
}`,
    },
];

// ── Scope summary rows ────────────────────────────────────────
const scopeSummaryRows = [
    { scope: "Block / Local",    lifetime: "Until block ends  }",      access: "Only inside that block",         example: "{ int x = 5; }" },
    { scope: "Function Local",   lifetime: "Until function returns",   access: "Only inside that function",      example: "void f() { int n; }" },
    { scope: "Static Local",     lifetime: "Whole program life",       access: "Only inside that function",      example: "static int count = 0;" },
    { scope: "Global",           lifetime: "Whole program life",       access: "All functions in file",          example: "int g = 10;  // top-level" },
    { scope: "Namespace",        lifetime: "Whole program life",       access: "Inside namespace or with ::",    example: "namespace App { int v; }" },
    { scope: "Class Static",     lifetime: "Whole program life",       access: "All instances, via Class::",     example: "static int total;" },
];

// ── Parameter passing comparison ─────────────────────────────
const paramComparison = [
    { method: "By Value",        syntax: "void f(int n)",        modifies: "❌ No",   cost: "Copy", use: "Primitives" },
    { method: "By Reference",    syntax: "void f(int& n)",       modifies: "✅ Yes",  cost: "None", use: "Large objects to modify" },
    { method: "By Const Ref",    syntax: "void f(const int& n)", modifies: "❌ No",   cost: "None", use: "Large objects read-only" },
    { method: "By Pointer",      syntax: "void f(int* p)",       modifies: "✅ Yes*", cost: "None", use: "Optional / nullable args" },
    { method: "By Const Pointer",syntax: "void f(const int* p)", modifies: "❌ No",   cost: "None", use: "Arrays, C API interop" },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function CppFunctionsScope() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [declTab,  setDeclTab]  = useState(0);
    const [invoTab,  setInvoTab]  = useState(0);
    const [scopeTab, setScopeTab] = useState(0);
    const [paramTab, setParamTab] = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ FUNCTIONS &amp; SCOPE
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    Understanding Functions and Scope in C++
                </Typography>
                {/* Topic chips */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["Function Declaration & Definition","Function Invocation","Local & Global Scope","Function Parameters"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{ bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)", border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem" }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 3 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.8, color: "rgba(255,255,255,0.8)" }}>
                        <strong style={{ color: "goldenrod" }}>Functions</strong> allow modular programming in C++ by grouping reusable code.{" "}
                        <strong style={{ color: "goldenrod" }}>Scope</strong> determines where variables and functions can be accessed.
                        Together they are the foundation of well-structured, maintainable C++ programs.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — FUNCTION DECLARATION & DEFINITION
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Function Declaration &amp; Definition
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <strong>declaration</strong> (prototype) tells the compiler a function exists.
                            A <strong>definition</strong> provides its actual code. In C++ you must declare
                            before you use — placing the definition above the call site also counts.
                        </Typography>

                        {/* Anatomy diagram */}
                        <ConceptCard title="Anatomy of a Function" color="#f0883e">
                            <CodeBlock>{`//   return_type    name      ( parameters )          { body }
//   ────────┐     ──┐         ──────────┐              ──────┐
             int   add    (int a, int b)              { return a + b; }
//                                ↑                    ↑
//                         formal parameters         function body
//
//   ┌─ return_type ──┐  Can be int, double, void, bool, string, auto…
//   ┌─ name ─────────┐  Descriptive verb: calculateArea, printReport
//   ┌─ parameters ───┐  0 or more: (type name, type name, …)
//   ┌─ body ─────────┐  Code block { }  —  must return matching type`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={declTab} onChange={(_, v) => setDeclTab(v)} tabs={declDefTabs.map(t => t.label)} />
                        <CodeBlock>{declDefTabs[declTab].code}</CodeBlock>

                        {/* Return types table */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>↩️</span>} title="Common Return Types" subtitle="Every non-void function must have a return statement that matches the declared type" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <TableHeader columns={["Return Type", "Example Signature", "Notes"]} cols="120px 1fr 1fr" />
                            {[
                                { type: "void",         sig: "void printMenu()",           note: "Returns nothing. Use bare 'return;' to exit early" },
                                { type: "int",          sig: "int countWords(string s)",   note: "Most common. Return 0 for success in main()" },
                                { type: "double",       sig: "double circleArea(double r)",note: "Floating-point results" },
                                { type: "bool",         sig: "bool isValid(int n)",        note: "Use for predicate / check functions" },
                                { type: "string",       sig: "string toUpper(string s)",   note: "Returns by value — copy is made" },
                                { type: "auto",         sig: "auto compute(int x)",        note: "C++14: compiler deduces return type from body" },
                                { type: "vector<int>",  sig: "vector<int> getEvenNums()",  note: "Return complex type — consider passing by ref instead" },
                            ].map((r, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: "0.83rem", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                    <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod" }}>{r.type}</Box>
                                    <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.8rem" }}>{r.sig}</Box>
                                    <Box sx={{ opacity: 0.7, fontSize: "0.82rem" }}>{r.note}</Box>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — FUNCTION INVOCATION
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Function Invocation
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <strong>Invoking</strong> (calling) a function transfers control to its body,
                            passing arguments that become the function's parameters. Control returns
                            to the caller when a <code style={{ color: "goldenrod" }}>return</code> statement
                            is reached, or the body ends (for void functions).
                        </Typography>

                        {/* Call stack diagram */}
                        <ConceptCard title="The Call Stack" color="#58a6ff">
                            <CodeBlock>{`// When you call a function, C++ pushes a STACK FRAME:
//
//   main()  calls  add()  calls  print()
//   ┌──────────────────────────────┐
//   │  print() frame  ← TOP       │  ← currently executing
//   │  int result = 7             │
//   ├──────────────────────────────┤
//   │  add() frame                │  ← waiting for print() to return
//   │  int a=3, int b=4           │
//   ├──────────────────────────────┤
//   │  main() frame  ← BOTTOM     │  ← waiting for add() to return
//   └──────────────────────────────┘
//
//  Each frame holds: local variables, parameters, return address
//  Stack overflows on infinite/deep recursion!  →  use iteration`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={invoTab} onChange={(_, v) => setInvoTab(v)} tabs={invocationTabs.map(t => t.label)} />
                        <CodeBlock>{invocationTabs[invoTab].code}</CodeBlock>

                        {/* Invocation patterns */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📞</span>} title="Invocation Patterns" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { title: "Store return value",    color: "#56d364", code: 'int result = add(3, 4);\nstd::string s = getName();' },
                                { title: "Pass as argument",       color: "#fbbf24", code: 'printResult(add(3, 4));\nsort(v.begin(), v.end(), cmp);' },
                                { title: "Ignore return value",    color: "#f0883e", code: 'add(3, 4);       // discarded\nprintf("hi");    // return ignored' },
                                { title: "Condition check",        color: "#a855f7", code: 'if (isValid(x)) { ... }\nwhile (!isEmpty(q)) { ... }' },
                            ].map((p, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${p.color}33`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Typography sx={{ fontSize: "0.78rem", color: p.color, fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>{p.title}</Typography>
                                    <CodeBlock sx={{ fontSize: "0.78rem", p: "12px 14px", "&::before": { content: '""' } }}>{p.code}</CodeBlock>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — LOCAL & GLOBAL SCOPE
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Local &amp; Global Scope
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <strong>Scope</strong> is the region of code where a name (variable, function, class)
                            is visible. C++ uses <em>lexical (static) scoping</em> — determined by where
                            the declaration appears in source code, not how the program runs.
                        </Typography>

                        {/* Scope visual */}
                        <ConceptCard title="Scope Nesting Visualised" color="#7ee787">
                            <CodeBlock>{`// ── Scopes nest like boxes ────────────────────────────────
// Outer scope names visible in inner scopes (unless shadowed)
// Inner scope names NOT visible in outer scopes

int  global = 1;           // ← visible everywhere ─────────────┐
                           //                                    │
void example() {           //                                    │
    int local = 2;         // ← visible inside example() ──┐   │
                           //                               │   │
    if (true) {            //                               │   │
        int block = 3;     // ← visible only in if-block ─┐│   │
        global;            // ✅  3 levels up             ││   │
        local;             // ✅  1 level up              ││   │
        block;             // ✅  same level              ││   │
    }                      //                              ┘│   │
    // block; ❌ out of scope                               │   │
    local;                 // ✅ still here                 │   │
    global;                // ✅                            │   │
}                          //                               ┘   │
// local; ❌ out of scope                                       │
global;                    // ✅ always visible ────────────────┘`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={scopeTab} onChange={(_, v) => setScopeTab(v)} tabs={scopeTabs.map(t => t.label)} />
                        <CodeBlock>{scopeTabs[scopeTab].code}</CodeBlock>

                        {/* Scope summary table */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📊</span>} title="Scope Quick Reference" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Scope Type", "Lifetime", "Accessible From", "Example"]} cols="140px 1fr 1fr 1fr" />}
                            {scopeSummaryRows.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.5, alignItems: "center" }}>
                                            <Chip label={row.scope} size="small" sx={{ bgcolor: "rgba(218,165,32,0.12)", color: "goldenrod", fontWeight: 700, fontSize: "0.7rem" }} />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mb: 0.5 }}>{row.lifetime} · {row.access}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{row.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: "0.83rem", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontWeight: 700, color: "goldenrod", fontFamily: "monospace", fontSize: "0.8rem" }}>{row.scope}</Box>
                                        <Box sx={{ opacity: 0.65, fontSize: "0.8rem" }}>{row.lifetime}</Box>
                                        <Box sx={{ opacity: 0.75, fontSize: "0.8rem" }}>{row.access}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.example}</Box>
                                    </Box>
                                )
                            )}
                        </Box>

                        {/* Best practices */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>✅</span>} title="Scope Best Practices" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { icon:"✅", color:"#56d364", title:"Declare as locally as possible",    body:"Reduce variable lifetime to the smallest necessary scope. Easier to reason about and avoids accidental reuse." },
                                { icon:"✅", color:"#56d364", title:"Prefer const over mutable globals",   body:"If global state is needed, make it const. Mutable globals create hard-to-trace bugs across large codebases." },
                                { icon:"⚠️", color:"#ffa657", title:"Avoid shadowing variable names",     body:"Reusing an outer name in an inner scope causes confusion. Enable compiler warnings: -Wshadow." },
                                { icon:"⚠️", color:"#ffa657", title:"Minimise global mutable state",      body:"Pass data through function parameters instead. Global state is the #1 source of coupling and bugs." },
                            ].map((tip, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${tip.color}33`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.75 }}>
                                        <Typography sx={{ fontSize: "1rem" }}>{tip.icon}</Typography>
                                        <Typography sx={{ fontWeight: 700, color: tip.color, fontSize: "0.82rem" }}>{tip.title}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.82rem", opacity: 0.7, lineHeight: 1.7 }}>{tip.body}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — FUNCTION PARAMETERS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. Function Parameters
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            How you <strong>pass arguments</strong> is one of the most important decisions in C++.
                            Pass-by-value copies data. Pass-by-reference avoids copies. Pass-by-const-reference
                            gives fast read-only access. Mastering this is key to both correctness and performance.
                        </Typography>

                        {/* Comparison summary */}
                        <ConceptCard title="Parameter Passing — Decision Guide" color="#e879f9">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr 1fr", gap: "8px", mb: 1 }}>
                                {paramComparison.map((r, i) => (
                                    <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", p: "10px 12px" }}>
                                        <Typography sx={{ fontWeight: 700, color: "#e879f9", fontSize: "0.78rem", mb: 0.5 }}>{r.method}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.72rem", mb: 0.5 }}>{r.syntax}</Typography>
                                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                            <Chip label={`Modifies: ${r.modifies}`} size="small" sx={{ fontSize: "0.65rem", height: 18, bgcolor: r.modifies.includes("Yes") ? "rgba(248,81,73,0.12)" : "rgba(86,211,100,0.12)", color: r.modifies.includes("Yes") ? "#f85149" : "#56d364" }} />
                                            <Chip label={`Cost: ${r.cost}`} size="small" sx={{ fontSize: "0.65rem", height: 18, bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.55)" }} />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", mt: 0.5 }}>{r.use}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={paramTab} onChange={(_, v) => setParamTab(v)} tabs={paramTabs.map(t => t.label)} />
                        <CodeBlock>{paramTabs[paramTab].code}</CodeBlock>

                        {/* Quick rules */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📐</span>} title="The Golden Rules for Parameter Passing" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[
                                { rule: "Primitive types (int, double, char, bool)",      action: "Pass by value",              code: "void f(int n)",          color: "#56d364" },
                                { rule: "Large objects you need to modify",               action: "Pass by reference",          code: "void f(MyClass& obj)",   color: "#f0883e" },
                                { rule: "Large objects you only need to READ",            action: "Pass by const reference",    code: "void f(const MyClass& obj)", color: "#58a6ff" },
                                { rule: "Optional / nullable arguments",                  action: "Pass by pointer",            code: "void f(int* ptr)",       color: "#a855f7" },
                                { rule: "Transferring ownership (move semantics)",        action: "Pass by rvalue reference",   code: "void f(MyClass&& obj)",  color: "#fbbf24" },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "10px", bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${row.color}22`, borderLeft: `3px solid ${row.color}`, borderRadius: "12px", px: 2, py: "12px", alignItems: "center" }}>
                                    <Typography sx={{ fontSize: "0.83rem", opacity: 0.8 }}>{row.rule}</Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.3)" }}>→</Typography>
                                        <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, color: row.color }}>{row.action}</Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{row.code}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ── Summary cheatsheet ── */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Quick Reference Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3 }}>
                            {[
                                {
                                    title: "Function Checklist", color: "#f0883e",
                                    items: ["✅ Prototype before use (or define above)", "✅ Return type matches return statement", "✅ void returns nothing (or bare return;)", "✅ Overloads differ in parameters, not return type", "✅ Defaults only on rightmost parameters", "✅ Recursive functions always have a base case"],
                                },
                                {
                                    title: "Scope Checklist", color: "#58a6ff",
                                    items: ["✅ Declare variables as late / locally as possible", "✅ Use const for globals — avoid mutable globals", "✅ Enable -Wshadow to catch accidental shadowing", "✅ Use :: to access global when shadowed by local", "✅ Prefer namespaces over global names", "✅ static local retains value between calls"],
                                },
                                {
                                    title: "Parameter Checklist", color: "#56d364",
                                    items: ["✅ Primitives → pass by value", "✅ Large objects to modify → T& ref", "✅ Large objects read-only → const T& ref", "✅ Optional args → T* pointer (check nullptr!)", "✅ Avoid output parameters — prefer return value", "✅ Prefer const references over raw pointers"],
                                },
                                {
                                    title: "Common Mistakes", color: "#f85149",
                                    items: ["❌ Using a variable outside its scope", "❌ Forgetting return in non-void function", "❌ Modifying a copy thinking it changes original", "❌ Dereferencing nullptr pointer parameter", "❌ Shadowing outer variable with same name", "❌ Recursion without base case → stack overflow"],
                                },
                            ].map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.82rem", opacity: 0.8, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
